import {
  LIVE_GOLD,
  LIVE_GOLD_DEEP,
  LIVE_PROGRESS_STRIPE,
  LIVE_TRACK_BG,
} from '@/utils/liveTrackerTheme';

/**
 * Live prop progress bar (Figma: target marker at ~80% so over-performance shows).
 * Fill + value bubble track currentResult / (threshold / 0.8).
 */

const TARGET_AT_FRACTION = 0.8;

export function formatPropStatNumber(value: number): string {
  if (!Number.isFinite(value)) return '0';
  if (Number.isInteger(value) || Math.abs(value - Math.round(value)) < 1e-9) {
    return String(Math.round(value));
  }
  return String(Number(value.toFixed(1)));
}

export function getPropProgressPositions(
  currentValue: number,
  threshold: number
): { fillPct: number; bubblePct: number; targetPct: number } {
  const safeThreshold = Math.max(threshold, 0.01);
  const maxScale = safeThreshold / TARGET_AT_FRACTION;
  const rawPct = (Math.max(0, currentValue) / maxScale) * 100;
  const clamped = Math.min(100, rawPct);
  return {
    fillPct: clamped,
    bubblePct: clamped,
    targetPct: TARGET_AT_FRACTION * 100,
  };
}

type PropStatProgressTrackerProps = {
  currentValue: number | null | undefined;
  threshold: number;
  /** Graded / live outcome for fill color. */
  outcome: 'pending' | 'hit' | 'miss' | 'void';
  className?: string;
  /** Longer CSS fill after a scoring Lottie (ms). Default 500. */
  fillDurationMs?: number;
  /** Gold striped fill for an in-progress Today's Game. */
  live?: boolean;
};

export default function PropStatProgressTracker({
  currentValue,
  threshold,
  outcome,
  className = '',
  fillDurationMs = 500,
  live = false,
}: PropStatProgressTrackerProps) {
  const current =
    currentValue != null && Number.isFinite(Number(currentValue)) ? Number(currentValue) : 0;
  const safeThreshold = Number(threshold);
  const { fillPct, bubblePct, targetPct } = getPropProgressPositions(
    current,
    Number.isFinite(safeThreshold) ? safeThreshold : 0.01
  );

  const isHit = outcome === 'hit';
  const isMiss = outcome === 'miss';
  const accent = live ? LIVE_GOLD : isHit ? '#53D337' : isMiss ? '#E9344A' : '#8a8a8a';
  const bubbleBg = live ? LIVE_GOLD_DEEP : isHit ? '#53D337' : isMiss ? '#E9344A' : '#4a4a4a';
  const bubbleFg = live ? LIVE_GOLD : '#ffffff';
  const trackBg = live ? LIVE_TRACK_BG : '#3a3a3a';

  return (
    <div className={`relative w-full pt-1 pb-4 pl-3 ${className}`}>
      {/* Track */}
      <div className="relative h-[6px] w-full rounded-full" style={{ backgroundColor: trackBg }}>
        <div
          className="absolute inset-y-0 left-0 overflow-hidden rounded-full transition-[width] ease-out"
          style={{
            width: `${fillPct}%`,
            backgroundColor: accent,
            backgroundImage: live ? LIVE_PROGRESS_STRIPE : undefined,
            transitionDuration: `${fillDurationMs}ms`,
          }}
        />

        {/* Target dashed marker at ~80% */}
        <div
          className="pointer-events-none absolute top-1/2 z-[1] -translate-x-1/2 -translate-y-1/2"
          style={{ left: `${targetPct}%` }}
          aria-hidden
        >
          <div
            className="h-3 w-0 border-l border-dashed border-white/90"
            style={{ borderLeftWidth: 1.5 }}
          />
        </div>

        {/* Current value bubble */}
        <div
          className="absolute top-1/2 z-[2] -translate-x-1/2 -translate-y-1/2 transition-[left] ease-out"
          style={{ left: `${bubblePct}%`, transitionDuration: `${fillDurationMs}ms` }}
        >
          <span
            className={`inline-flex min-w-[28px] items-center justify-center px-1.5 py-[2px] text-[11px] font-bold leading-none tabular-nums shadow-sm ${
              live ? 'rounded-[6px]' : 'rounded-full text-white'
            }`}
            style={{
              backgroundColor: bubbleBg,
              color: bubbleFg,
              fontFamily: 'Inter, sans-serif',
            }}
          >
            {formatPropStatNumber(current)}
          </span>
        </div>
      </div>

      {/* Threshold label under dashed marker */}
      <div
        className="pointer-events-none absolute bottom-0 -translate-x-1/2 text-[10px] font-medium leading-none text-white/90 tabular-nums"
        style={{
          left: `${targetPct}%`,
          fontFamily: 'Inter, sans-serif',
        }}
      >
        {formatPropStatNumber(threshold)}
      </div>
    </div>
  );
}
